import React, { useCallback, useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, InputBase, Typography } from '@material-ui/core';
import { Close, FileCopy, Forum, Person } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { CommunicationsOptionsMenu } from './CommunicationsOptionsMenu';
import { ExpandablePanel, ExpandToggleButton } from './ExpandablePanel';
import { FallbackAvatar } from '../../general/user-profile/components/Avatar';
import { SmsThread } from '../../../common/communications/SmsThread';
import {
  CommunicationsPerson,
  ConversationMessage,
  GetConversationRequest,
  GetConversationResponse,
  InboxReply,
  SendSmsRequest,
  personInitials,
  personName,
} from './communications-model';
import { getSmsSegmentInfo } from '../../../common/sms/sms-segments';
import { useEndpointFetch } from '../../../global/network/useEndpointFetch';
import { useSnackbar } from '../../../global/config/useSnackbar';
import { useUser } from '../../../global/auth/useUser';

export interface OpenProfileRequest {
  profileId: string;
  userId: string;
  openOn?: string;
}

interface InboxThreadPanelProps {
  reply: InboxReply;
  candidates?: CommunicationsPerson[];
  onClose: () => void;
  onOpenProfile: (profile: OpenProfileRequest) => void;
}

export const InboxThreadPanel = ({ reply, candidates, onClose, onOpenProfile }: InboxThreadPanelProps) => {
  const [expanded, setExpanded] = useState(false);
  const [draft, setDraft] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [messages, setMessages] = useState<ConversationMessage[] | undefined>(undefined);
  const draftSegments = getSmsSegmentInfo(draft);
  const { setSnackbar } = useSnackbar();
  const { user } = useUser();
  const getConversation = useEndpointFetch<GetConversationRequest, GetConversationResponse>('/em/comms/conversation');
  const sendSms = useEndpointFetch<SendSmsRequest>('/vm/comms/send-sms');

  const match = candidates && candidates.length === 1 ? candidates[0] : undefined;
  const conversationUserId = reply.userId ?? match?.userId ?? undefined;
  const canReply = Boolean(match && match.profileId);

  const loadConversation = useCallback(async () => {
    const response = await getConversation.run(
      conversationUserId ? { userId: conversationUserId } : { contactNumber: reply.contactNumber }
    );
    setMessages(response && response.ok ? response.data.data?.messages ?? [] : []);
  }, [conversationUserId, reply]);

  useEffect(() => {
    loadConversation();
  }, [loadConversation]);

  const handleSend = async () => {
    if (!match || !draft.trim()) return;

    setIsSending(true);
    try {
      const response = await sendSms.run({
        profileIds: [match.profileId],
        sentBy: user.profileId,
        message: draft.trim(),
        senderName: `${user.firstName} ${user.lastName}`,
        allowReplies: true,
      });

      if (response && response.ok) {
        setDraft('');
        setSnackbar({ message: 'Reply sent', variant: 'success', open: true });
        loadConversation();
      } else {
        setSnackbar({ message: 'Failed to send reply', variant: 'error', open: true });
      }
    } catch (error) {
      setSnackbar({ message: 'Failed to send reply', variant: 'error', open: true });
    } finally {
      setIsSending(false);
    }
  };
  const title = match ? personName(match) : reply.contactNumber;
  const subtitle = match ? reply.contactNumber : 'No matching volunteer';
  const senderLabel = match ? match.preferredName ?? match.firstName : 'Unknown sender';
  const thread: ConversationMessage[] =
    messages && messages.length > 0
      ? messages
      : [{ direction: 'inbound', id: reply.inboundSmsId, body: reply.body, at: reply.receivedAt }];
  const openable = Boolean(match && match.userId);

  return (
    <ExpandablePanel expanded={expanded} onCollapse={() => setExpanded(false)}>
      <Box
        flexShrink={0}
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        padding='14px 24px'
        style={{ borderBottom: '1px solid #EFEFEF' }}
      >
        <Box display='flex' alignItems='center' minWidth={0}>
          <FallbackAvatar size={40} initials={match ? personInitials(match) : null} style={{ flexShrink: 0 }} />
          <Box paddingLeft='14px' minWidth={0}>
            <Typography
              noWrap
              onClick={() => {
                if (match && match.userId) onOpenProfile({ profileId: match.profileId, userId: match.userId });
              }}
              style={{
                fontSize: 16,
                fontWeight: 600,
                color: '#444444',
                cursor: openable ? 'pointer' : 'default',
              }}
            >
              {title}
            </Typography>
            <Typography noWrap style={{ fontSize: 12, color: '#646464', marginTop: 2 }}>
              {subtitle}
            </Typography>
          </Box>
        </Box>

        <Box display='flex' alignItems='center' style={{ gap: 8, flexShrink: 0 }}>
          <CommunicationsOptionsMenu
            options={[
              {
                label: 'Open profile',
                Icon: Person,
                disabled: !openable,
                onClick: () => {
                  if (match && match.userId) onOpenProfile({ profileId: match.profileId, userId: match.userId });
                },
              },
              {
                label: 'View all communications',
                Icon: Forum,
                disabled: !openable,
                onClick: () => {
                  if (match && match.userId) {
                    onOpenProfile({ profileId: match.profileId, userId: match.userId, openOn: 'communications' });
                  }
                },
              },
              {
                label: 'Copy number',
                Icon: FileCopy,
                onClick: () => navigator.clipboard.writeText(reply.contactNumber),
              },
            ]}
          />
          <ExpandToggleButton expanded={expanded} onToggle={() => setExpanded((value) => !value)} />
          <IconButton size='small' aria-label='Close conversation' onClick={onClose}>
            <Close style={{ fontSize: 18, color: '#646464' }} />
          </IconButton>
        </Box>
      </Box>

      <Box
        flexGrow={1}
        padding='22px 24px'
        display='flex'
        flexDirection='column'
        minHeight={0}
        style={{ backgroundColor: '#F9F9F9', overflowY: 'auto', boxSizing: 'border-box' }}
      >
        {messages === undefined && (
          <Box display='flex' justifyContent='center' marginBottom='14px'>
            <CircularProgress size={18} />
          </Box>
        )}
        <SmsThread
          messages={thread.map((message) => ({
            id: `${message.direction}-${message.id}`,
            body: message.body,
            inbound: message.direction === 'inbound',
            at: message.at,
            author: message.direction === 'inbound' ? senderLabel : message.sentBy === user.profileId ? 'You' : 'Sent',
          }))}
        />
      </Box>

      <Box
        flexShrink={0}
        padding='14px 24px 18px 24px'
        style={{ borderTop: '1px solid #EFEFEF', backgroundColor: '#FFFFFF', boxSizing: 'border-box' }}
      >
        <Box display='flex' alignItems='center' justifyContent='flex-end' marginBottom='10px'>
          <Typography style={{ fontSize: 11, color: '#646464' }}>
            {`${draftSegments.length} / ${draftSegments.perSegment * Math.max(1, draftSegments.segments)} · ${Math.max(
              1,
              draftSegments.segments
            )} segment${draftSegments.segments > 1 ? 's' : ''}`}
          </Typography>
        </Box>
        <Box display='flex' alignItems='center' style={{ gap: 8 }}>
          <InputBase
            value={draft}
            disabled={!canReply || isSending}
            placeholder={canReply ? 'Write a reply' : 'Replies need a matched volunteer'}
            onChange={(e) => setDraft(e.target.value)}
            style={{
              flexGrow: 1,
              border: '0.5px solid #d9d9d9',
              borderRadius: 4,
              padding: '10px 12px',
              fontSize: 14,
              color: '#444444',
            }}
          />
          <TabletButton
            variant='contained'
            color='primary'
            disabled={!canReply || isSending || draft.trim() === ''}
            onClick={handleSend}
          >
            {isSending ? 'Sending' : 'Send'}
          </TabletButton>
        </Box>
      </Box>
    </ExpandablePanel>
  );
};
